import React, { createContext, useContext, useState, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { getLocalStorage, setLocalStorage, LOCAL_STORAGE_KEYS } from "../utils/localStorageUtil";
import { getTimezoneLabel } from "../utils/utility";
import { useGlobalFilters } from "./GlobalFilterProvider";

export const TZ_PREFERENCES = {
  SITE: "site",
  LOCAL: "local",
  UTC: "utc",
};

export const TZ_OPTIONS = [
  { value: TZ_PREFERENCES.SITE, label: "Site Timezone" },
  { value: TZ_PREFERENCES.LOCAL, label: "Local Timezone" },
  { value: TZ_PREFERENCES.UTC, label: "UTC" },
];

const getBrowserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return "UTC";
  }
};

const loadPreference = () => {
  try {
    const saved = getLocalStorage(LOCAL_STORAGE_KEYS.TZ_PREFERENCE);
    if (saved && typeof saved === "string" && Object.values(TZ_PREFERENCES).includes(saved)) return saved;
    if (saved && saved.preference && Object.values(TZ_PREFERENCES).includes(saved.preference)) return saved.preference;
    return TZ_PREFERENCES.SITE;
  } catch {
    return TZ_PREFERENCES.SITE;
  }
};

const TimezoneContext = createContext(null);

export const TimezoneProvider = ({ children }) => {
  const [preference, setPreferenceState] = useState(loadPreference);
  const { siteTimezone } = useGlobalFilters();

  const setPreference = useCallback(pref => {
    setPreferenceState(pref);
    setLocalStorage(LOCAL_STORAGE_KEYS.TZ_PREFERENCE, pref);
  }, []);

  const browserTimezone = useMemo(() => getBrowserTimezone(), []);

  const effectiveTimezone = useMemo(() => {
    switch (preference) {
      case TZ_PREFERENCES.LOCAL:
        return browserTimezone;
      case TZ_PREFERENCES.UTC:
        return "UTC";
      case TZ_PREFERENCES.SITE:
      default:
        return siteTimezone || "UTC";
    }
  }, [preference, siteTimezone, browserTimezone]);

  const timezoneLabel = useMemo(() => {
    const base = getTimezoneLabel(effectiveTimezone);
    switch (preference) {
      case TZ_PREFERENCES.LOCAL:
        return `${base} (Local)`;
      case TZ_PREFERENCES.UTC:
        return "UTC";
      case TZ_PREFERENCES.SITE:
      default:
        return base;
    }
  }, [effectiveTimezone, preference]);

  const value = useMemo(
    () => ({
      preference,
      effectiveTimezone,
      timezoneLabel,
      browserTimezone,
      siteTimezone: siteTimezone || "UTC",
      setPreference,
    }),
    [preference, effectiveTimezone, timezoneLabel, browserTimezone, siteTimezone, setPreference]
  );

  return <TimezoneContext.Provider value={value}>{children}</TimezoneContext.Provider>;
};

TimezoneProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useTimezone() {
  const ctx = useContext(TimezoneContext);
  if (!ctx) throw new Error("useTimezone must be used within TimezoneProvider");
  return ctx;
}

export default TimezoneContext;
